import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"
import { FormProvider, useFieldArray, useForm } from "react-hook-form"

import { CustomerProductFileForm } from "./CustomerProductFileForm"
import type { CustomerProductFileArrayFormValues, CustomerProductFileFormValues } from "./schemas"
import { customerProductFileArrayFormSchema } from "./schemas"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { IMAGE_MIME_TYPES_WITH_PDF_AND_DOC } from "@/components/upload/constants"
import useHandleImageFileUpload from "@/components/upload/hooks/useHandleFileUpload"
import { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface CustomerProductFileDialogProps {
  onClose: () => void
  onRefresh: () => void
  companyProductAssociationLocationId: string
}

const BLANK_FILE = {
  file: null,
  fileDescription: "",
  fileType: FieldGroupEnum.ProductCoa,
}

export const CustomerProductFileDialog: FC<CustomerProductFileDialogProps> = ({
  onClose,
  onRefresh,
  companyProductAssociationLocationId,
}) => {
  const [isUploading, setIsUploading] = useState(false)
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const methods = useForm<CustomerProductFileArrayFormValues>({
    resolver: zodResolver(customerProductFileArrayFormSchema),
    defaultValues: {
      files: [BLANK_FILE],
    },
    mode: "onChange",
  })

  const { control, watch, formState } = methods
  const { isDirty, isSubmitting, isValid } = formState

  const {
    fields,
    append: appendNewFile,
    remove,
  } = useFieldArray({
    control,
    name: "files",
  })

  const files = watch("files")
  const showAddButton = files.every((field) => field.file !== null && !!field.fileType) && files.length < 5

  const { uploadFile } = useHandleImageFileUpload({
    referenceTableName: ReferenceTableName.CustomerProductPreference,
    allowedFileTypes: IMAGE_MIME_TYPES_WITH_PDF_AND_DOC,
  })

  const handleAddFile = (): void => {
    appendNewFile(BLANK_FILE)
  }

  const handleRemoveFile = (index: number): void => {
    if (fields.length > 1) {
      remove(index)
    }
  }

  const canSubmit = isValid && isDirty && !isSubmitting

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return

    setIsUploading(true)

    try {
      const filesToUpload = files.filter(
        (
          fileData
        ): fileData is Required<Pick<CustomerProductFileFormValues, "file" | "fileType">> &
          CustomerProductFileFormValues => !!fileData?.file && !!fileData?.fileType
      )

      await Promise.all(
        filesToUpload.map((fileData) =>
          uploadFile({
            file: fileData.file as File,
            type: fileData.fileType as FieldGroupEnum,
            referenceId: companyProductAssociationLocationId,
            label: fileData.fileDescription ?? "",
          })
        )
      )

      notify({
        message: "Files uploaded",
        type: NotificationType.Success,
      })

      onRefresh()
      setIsUploading(false)
      onClose()
    } catch {
      setIsUploading(false)
      notify({
        message: "Failed to upload files",
        type: NotificationType.Error,
      })
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth data-testid='customer-product-file-dialog'>
      <DialogHeader title='Attach File' onClose={onClose} />
      <DialogContent classes={{ root: "p-10 pb-4" }}>
        <FormProvider {...methods}>
          <CustomerProductFileForm fields={fields} onRemove={handleRemoveFile} />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button onClick={onClose} appearance='outlined' data-testid='customer-product.file-dialog.cancel-button'>
          Cancel
        </Button>
        <Box className='flex justify-end'>
          {showAddButton && (
            <Button startIcon={<AddOutlined />} appearance='text' onClick={handleAddFile}>
              Add Another File
            </Button>
          )}
          <Button
            type='submit'
            loading={isUploading}
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid='customer-product.file-dialog.submit-button'
          >
            Done
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
